/*
 * SPDX-FileCopyrightText: 2023 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ReactCodeMirror from '@uiw/react-codemirror'

import { cypressAttribute, cypressId } from '../../../utils/cypress-attribute'
import { findLanguageByCodeBlockName } from '../../markdown-renderer/extensions/_base-classes/code-block-markdown-extension/find-language-by-code-block-name'
import { languages } from '@codemirror/language-data'
import { lintGutter } from '@codemirror/lint'
import { MaxLengthWarning } from './max-length-warning/max-length-warning'
import { markdown, markdownLanguage } from '@codemirror/lang-markdown'
import { markdownLinter } from './linter/markdown-linter'
import { oneDark } from '@codemirror/theme-one-dark'
import { ORIGIN, useBaseUrl } from '@hooks/common/use-base-url'
import { StatusBar } from './status-bar/status-bar'
import type { ScrollProps, ScrollState } from '../synced-scroll/scroll-props'
import { setEditImageModal } from '@redux/format-checker/formatChecker'
// import { useMayEdit } from '@hooks/common/use-may-edit'
import { setNoteContent } from '@redux/note-details/methods'
import { ToolBar } from './tool-bar/tool-bar'
import { useDarkModeState } from '@hooks/common/use-dark-mode-state'
import { imageHelperApi } from '@services/imageHelper'
import { setEditorDocChange, setScrollSource } from '@redux/editor/methods'
import { ScrollSource } from '../editor-page-content'
import { useApplicationState } from '@hooks/common/use-application-state'
import { useCodeMirrorAutocompletionsExtension } from './hooks/codemirror-extensions/use-code-mirror-autocompletions-extension'
import { useCodeMirrorFileInsertExtension } from './hooks/codemirror-extensions/use-code-mirror-file-insert-extension'
import { useCodeMirrorLineWrappingExtension } from './hooks/codemirror-extensions/use-code-mirror-line-wrapping-extension'
// import { useCodeMirrorRemoteCursorsExtension } from './hooks/codemirror-extensions/use-code-mirror-remote-cursor-extensions'
import { useCodeMirrorScrollWatchExtension } from './hooks/codemirror-extensions/use-code-mirror-scroll-watch-extension'
import { useCodeMirrorSpellCheckExtension } from './hooks/codemirror-extensions/use-code-mirror-spell-check-extension'
import { useOnImageUploadFromRenderer } from './hooks/image-upload-from-renderer/use-on-image-upload-from-renderer'
import { useCodeMirrorTablePasteExtension } from './hooks/table-paste/use-code-mirror-table-paste-extension'
// import { useApplyScrollState } from './hooks/use-apply-scroll-state'
import { useCursorActivityCallback } from './hooks/use-cursor-activity-callback'
// import { useDisconnectOnUserLoginStatusChange } from './hooks/use-disconnect-on-user-login-status-change'
import { useUpdateCodeMirrorReference } from './hooks/use-update-code-mirror-reference'
// import { useBindYTextToRedux } from './hooks/yjs/use-bind-y-text-to-redux'
// import { useCodeMirrorYjsExtension } from './hooks/yjs/use-code-mirror-yjs-extension'
// import { useOnMetadataUpdated } from './hooks/yjs/use-on-metadata-updated'
// import { useOnNoteDeleted } from './hooks/yjs/use-on-note-deleted'
// import { useRealtimeConnection } from './hooks/yjs/use-realtime-connection'
// import { useRealtimeDoc } from './hooks/yjs/use-realtime-doc'
// import { useReceiveRealtimeUsers } from './hooks/yjs/use-receive-realtime-users'
// import { useSendRealtimeActivity } from './hooks/yjs/use-send-realtime-activity'
// import { useYDocSyncClientAdapter } from './hooks/yjs/use-y-doc-sync-client-adapter'
import { useLinter } from './linter/linter'
import { useUiNotifications } from '@components/notifications/ui-notification-boundary'
import { updateCommentPositionOnChange, updatePrevTextAndSelection } from '@redux/comments/methods'
import { useLineBasedFromPosition } from './hooks/use-line-based-position'
import { useBeforeUnload } from 'react-use'

import styles from './extended-codemirror/codemirror.module.scss'
import { Logger } from '@utils/logger'
import { setDraftContent } from '@redux/note-draft/noteDraft'

export type EditorPaneProps = ScrollProps

const log = new Logger('EditorPane')

/**
 * Renders the text editor pane of the editor.
 * The used editor is {@link ReactCodeMirror code mirror}.
 *
 * @param scrollState The current {@link ScrollState}
 * @param onScroll The callback to update the {@link ScrollState}
 * @param onMakeScrollSource The callback to request to become the scroll source.
 * @external {ReactCodeMirror} https://npmjs.com/@uiw/react-codemirror
 */
export const EditorPane: React.FC<EditorPaneProps> = () => {
  // useApplyScrollState(scrollState)
  const { draftMode } = useApplicationState((state) => ({ draftMode: state.noteDetails.draftMode }))
  const { noteContent, selection } = useApplicationState((state) => ({
    noteContent: draftMode && state.noteDraft.content !== null ? state.noteDraft.content : state.noteDetails.markdownContent.plain,
    selection: state.noteDetails.selection,
  }));
  const { scrollSource, hasEditorDocChange }= useApplicationState((state) => ({
    scrollSource: state.editorConfig.scrollSource,
    hasEditorDocChange: state.editorConfig.editorDocChange
  }))


  // const messageTransporter = useRealtimeConnection()

  // useDisconnectOnUserLoginStatusChange(messageTransporter)

  // const realtimeDoc = useRealtimeDoc()
  useBeforeUnload(hasEditorDocChange, 'Changes that you made may not be saved.');

  const lineBasedPosition = useLineBasedFromPosition();

  const debounce = (func: Function, delay: number) => {
    if (scrollSource === ScrollSource.RENDERER) {
      return;
    }
    let timeout: NodeJS.Timeout;
    
    return (...args: any) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // editor scroll
  const onEditorScroll = debounce((newScrollState: ScrollState) => {
    let currentLine = newScrollState.firstLineInView;

    let elRenderer = document.querySelector(
      `[data-startline="${currentLine}"]`
    );
    let limit = 5;
    // case when element is not found
    // check for next 5 lines
    while (!elRenderer && limit > 0) {
      currentLine++;
      elRenderer = document.querySelector(`[data-startline="${currentLine}"]`);
      limit--;
    }

    if (elRenderer) {
      elRenderer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, 200);

  const onEditorClickScroll = useCallback(() => {
    const selectedLine = lineBasedPosition.line + 1;
    let elRenderer = document.querySelector(`[data-startline="${selectedLine}"]`)
    if (elRenderer)
      elRenderer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [lineBasedPosition]);

  const editorScrollExtension = useCodeMirrorScrollWatchExtension(onEditorScroll)
  const tablePasteExtensions = useCodeMirrorTablePasteExtension()
  const fileInsertExtension = useCodeMirrorFileInsertExtension()
  const spellCheckExtension = useCodeMirrorSpellCheckExtension()
  const lineWrappingExtension = useCodeMirrorLineWrappingExtension()
  const cursorActivityExtension = useCursorActivityCallback()
  const autoCompletionExtension = useCodeMirrorAutocompletionsExtension()
  const dispatch = useDispatch()
  const { dispatchUiNotification } = useUiNotifications()

  const updateViewContextExtension = useUpdateCodeMirrorReference()

  // const remoteCursorsExtension = useCodeMirrorRemoteCursorsExtension(messageTransporter)

  const linterExtension = useLinter()

  // const syncAdapter = useYDocSyncClientAdapter(messageTransporter, realtimeDoc)
  // const yjsExtension = useCodeMirrorYjsExtension(realtimeDoc, syncAdapter)

  const [
    imageDataTrigger,
    {
      isLoading: imageDataLoader,
      data: singleImageObj,
      isFetching: imageDataFetch,
      status: imageDataStatus,
      error: imageDataError
    }
  ] = imageHelperApi.endpoints.getImageDataById.useLazyQuery();
  const userData = useApplicationState(state => state.user);
  // useOnMetadataUpdated(messageTransporter)
  // useOnNoteDeleted(messageTransporter)

  // useBindYTextToRedux(realtimeDoc)
  // useReceiveRealtimeUsers(messageTransporter)
  // useSendRealtimeActivity(messageTransporter)

  const extensions = useMemo(
    () => [
      linterExtension,
      lintGutter(),
      markdownLinter(dispatch, imageDataTrigger, userData?.hasImageToolsAccess),
      markdown({
        base: markdownLanguage,
        codeLanguages: (input) => findLanguageByCodeBlockName(languages, input)
      }),
      // remoteCursorsExtension,
      lineWrappingExtension,
      editorScrollExtension,
      tablePasteExtensions,
      fileInsertExtension,
      autoCompletionExtension,
      cursorActivityExtension,
      updateViewContextExtension,
      // yjsExtension,
      spellCheckExtension
    ],
    [
      linterExtension,
      // remoteCursorsExtension,
      autoCompletionExtension,
      editorScrollExtension,
      tablePasteExtensions,
      fileInsertExtension,
      cursorActivityExtension,
      updateViewContextExtension,
      // yjsExtension,
      spellCheckExtension,
      lineWrappingExtension,
      dispatch,
      imageDataTrigger,
      userData
    ]
  )

  useOnImageUploadFromRenderer()

  const ligaturesEnabled = useApplicationState((state) => state.editorConfig.ligatures)
  const codeMirrorClassName = useMemo(
    () => `overflow-hidden ${styles.extendedCodemirror} h-100 ${ligaturesEnabled ? '' : styles['no-ligatures']}`,
    [ligaturesEnabled]
  )

  const { t } = useTranslation()
  const darkModeActivated = useDarkModeState()
  const editorOrigin = useBaseUrl(ORIGIN.EDITOR)
  // const isSynced = useApplicationState((state) => state.realtimeStatus.isSynced)
  const isSynced = true
  // const mayEdit = useMayEdit()
  const mayEdit = true

  // useEffect(() => {
  //   const listener = messageTransporter.doAsSoonAsConnected(() => messageTransporter.sendReady())
  //   return () => {
  //     listener.off()
  //   }
  // }, [messageTransporter])

  useEffect(() => {
    if (imageDataStatus === 'uninitialized') return
    if (imageDataStatus === 'rejected') {
      dispatch(setEditImageModal(false))
      dispatchUiNotification(
        'Image Data Fetch Failed',
        imageDataError?.data?.error,
        {}
      )
    }
  }, [imageDataStatus, imageDataError, dispatchUiNotification])

  const handleEditorChange = useCallback((editorContent: string) => {
    setEditorDocChange(true);
    if (draftMode) {
      dispatch(setDraftContent(editorContent))
    } else {
      updatePrevTextAndSelection(selection, noteContent);
      setNoteContent(editorContent)
      updateCommentPositionOnChange()
    }
  }, [
    updatePrevTextAndSelection,
    updateCommentPositionOnChange,
    setNoteContent,
    selection,
    noteContent
  ])

  return (
    <div
      className={`d-flex flex-column h-100 position-relative`}
      // onTouchStart={onMakeScrollSource}
      // onMouseEnter={onMakeScrollSource}
      {...cypressId('editor-pane')}
      {...cypressAttribute('editor-ready', String(updateViewContextExtension !== null && isSynced && mayEdit))}>
      <MaxLengthWarning />
      <ToolBar
        imageData={{
          imageDataTrigger: imageDataTrigger!,
          singleImageObj: singleImageObj!,
          imageDataStatus: imageDataStatus!,
          imageDataError: imageDataError!,
          imageDataLoader: imageDataLoader!,
          imageDataFetch: imageDataFetch!
        }}
        hasImageToolsAccess={userData?.hasImageToolsAccess}
      />
      <ReactCodeMirror
        onMouseOver={() => scrollSource === ScrollSource.RENDERER ? setScrollSource(ScrollSource.EDITOR) : null}
        onClick={onEditorClickScroll}
        value={noteContent}
        editable={updateViewContextExtension !== null && isSynced && mayEdit}
        placeholder={t('editor.placeholder', { host: editorOrigin }) ?? ''}
        extensions={extensions}
        width={'100%'}
        height={'100%'}
        maxHeight={'100%'}
        maxWidth={'100%'}
        basicSetup={true}
        onChange={handleEditorChange}
        className={codeMirrorClassName}
        theme={darkModeActivated ? oneDark : undefined}
      />
      {
       noteContent.length === 0  ? <div className='text-center text-danger'>Editor is empty</div> :
        <StatusBar />
      }
    </div>
  )
}
